import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Link2, RefreshCw, X } from 'lucide-react';
import { useToast } from '../Common/context/ToastContext';
import {
  getAdminCpPaymentLinkAccess,
  updateAdminCpPaymentLinkAccess,
} from './api/cp';

const PAYMENT_LINK_ACCESS_UI_ENABLED = ['1', 'true', 'yes', 'y'].includes(
  String(process.env.REACT_APP_CP_PAYMENT_LINKS_ENABLED || '').trim().toLowerCase(),
);

function getPresentation(access, loading, error) {
  if (loading) return { label: '조회 중', className: 'bg-slate-100 text-slate-600' };
  if (error || !access) return { label: '확인 실패', className: 'bg-red-50 text-red-600' };
  if (access.globalEnabled !== true) return { label: '전역 비활성', className: 'bg-slate-100 text-slate-600' };
  if (String(access.cpStatus || '').toUpperCase() !== 'ACTIVE') return { label: '가맹점 비활성', className: 'bg-amber-50 text-amber-700' };
  if (access.enabled === true) return { label: '사용 가능', className: 'bg-emerald-50 text-emerald-700' };
  if (access.configured !== true) return { label: '미설정', className: 'bg-slate-100 text-slate-600' };
  return { label: '허용 안 함', className: 'bg-slate-100 text-slate-600' };
}

function getDescription(access, error) {
  if (error) return 'URL 결제 설정을 확인하지 못했습니다.';
  if (!access) return 'URL 결제 설정을 확인하고 있습니다.';
  if (access.globalEnabled !== true) return 'URL 결제 기능이 전역에서 비활성화되어 있습니다.';
  if (String(access.cpStatus || '').toUpperCase() !== 'ACTIVE') return '활성 상태의 가맹점만 URL 결제를 사용할 수 있습니다.';
  if (access.configured !== true) return '';
  if (access.enabled === true) return '고객에게 카드 결제 링크를 발급하고 문자로 전송할 수 있습니다.';
  return '이 가맹점에서는 고객 결제 링크를 발급할 수 없습니다.';
}

export default function PaymentLinkAccessSetting({ cpPkId, createMode = false }) {
  const { showToast } = useToast();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(!createMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingEnabled, setPendingEnabled] = useState(null);

  const loadAccess = useCallback(async () => {
    if (createMode || !cpPkId) return;
    setLoading(true);
    setError('');
    try {
      setAccess((await getAdminCpPaymentLinkAccess(cpPkId)) || null);
    } catch (e) {
      setAccess(null);
      setError(e.message || 'URL 결제 설정을 확인하지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [cpPkId, createMode]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const presentation = useMemo(
    () => getPresentation(access, loading, error),
    [access, loading, error],
  );

  if (!PAYMENT_LINK_ACCESS_UI_ENABLED) return null;

  if (createMode) {
    return (
      <section className="col-span-2 mt-2 rounded-xl border border-[#e5e8eb] bg-[#f9fafb] p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-3">
            <span className="mt-0.5 rounded-lg bg-white p-2 text-[#8b95a1]"><Link2 size={17} /></span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[14px] font-semibold text-[#333d4b]">고객 URL 결제</h3>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">등록 후 설정</span>
              </div>
              <p className="mt-1 text-[12px] leading-5 text-[#8b95a1]">가맹점 등록을 완료한 뒤 수정 화면에서 사용 여부를 설정할 수 있습니다.</p>
            </div>
          </div>
          <span role="switch" aria-checked="false" aria-disabled="true" className="relative mt-1 h-6 w-11 shrink-0 rounded-full bg-[#d1d6db] opacity-70">
            <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm" />
          </span>
        </div>
      </section>
    );
  }

  const cpActive = String(access?.cpStatus || '').toUpperCase() === 'ACTIVE';
  const toggleDisabled = loading || saving || !!error || !access || access.globalEnabled !== true || !cpActive;
  const checked = access?.cpEnabled === true;

  const saveAccess = async () => {
    if (pendingEnabled === null || saving) return;
    const nextEnabled = pendingEnabled;
    setSaving(true);
    try {
      await updateAdminCpPaymentLinkAccess(cpPkId, nextEnabled);
      await loadAccess();
      setPendingEnabled(null);
      showToast(nextEnabled ? '고객 URL 결제를 허용했습니다.' : '고객 URL 결제를 중지했습니다.');
    } catch (e) {
      showToast(e.message || 'URL 결제 설정을 변경하지 못했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="col-span-2 mt-2 rounded-xl border border-[#e5e8eb] bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-3">
            <span className="mt-0.5 rounded-lg bg-[var(--brand-primary-soft)] p-2 text-[var(--brand-primary)]"><Link2 size={17} /></span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[14px] font-semibold text-[#333d4b]">고객 URL 결제</h3>
                <span className={'rounded-full px-2 py-1 text-[11px] font-semibold ' + presentation.className}>{presentation.label}</span>
              </div>
              {getDescription(access, error) && (
                <p className={'mt-1 text-[12px] leading-5 ' + (error ? 'text-red-600' : 'text-[#8b95a1]')}>
                  {getDescription(access, error)}
                </p>
              )}
              {error && (
                <button type="button" onClick={loadAccess} disabled={loading} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--brand-primary)] disabled:opacity-50">
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> 다시 조회
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="고객 URL 결제 허용"
            aria-checked={checked}
            disabled={toggleDisabled}
            onClick={() => setPendingEnabled(!checked)}
            className={'relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors ' + (checked ? 'bg-[var(--brand-primary)]' : 'bg-[#d1d6db]') + ' disabled:cursor-not-allowed disabled:opacity-50'}
          >
            <span className={'absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-all ' + (checked ? 'left-6' : 'left-1')} />
          </button>
        </div>
      </section>

      {pendingEnabled !== null && (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/50 px-4" onMouseDown={() => { if (!saving) setPendingEnabled(null); }}>
          <div className="w-full max-w-[420px] rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-[#f2f4f6] px-6 py-5">
              <div className="flex gap-3">
                <span className={'rounded-full p-2 ' + (pendingEnabled ? 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]' : 'bg-red-50 text-red-600')}>
                  {pendingEnabled ? <Link2 size={18} /> : <AlertTriangle size={18} />}
                </span>
                <div>
                  <h3 className="text-[18px] font-semibold text-[#191f28]">{pendingEnabled ? 'URL 결제를 허용하시겠습니까?' : 'URL 결제를 중지하시겠습니까?'}</h3>
                  <p className="mt-2 text-[13px] leading-6 text-[#6b7684]">
                    {pendingEnabled
                      ? '해당 가맹점에서 고객 결제 링크를 생성하고 문자로 전송할 수 있습니다.'
                      : '신규 결제 링크 발급이 중단되며, 기존에 발급된 미결제 링크도 사용할 수 없게 됩니다.'}
                  </p>
                </div>
              </div>
              <button type="button" disabled={saving} onClick={() => setPendingEnabled(null)} className="text-[#8b95a1] disabled:opacity-50"><X size={20} /></button>
            </div>
            <div className="flex gap-3 px-6 py-5">
              <button type="button" disabled={saving} onClick={() => setPendingEnabled(null)} className="flex-1 rounded-xl bg-[#e5e8eb] py-3 text-[14px] font-semibold text-[#4e5968] disabled:opacity-50">취소</button>
              <button type="button" disabled={saving} onClick={saveAccess} className={'flex-1 rounded-xl py-3 text-[14px] font-semibold text-white disabled:opacity-50 ' + (pendingEnabled ? 'bg-[var(--brand-primary)]' : 'bg-red-600 hover:bg-red-700')}>
                {saving ? '처리 중...' : pendingEnabled ? 'URL 결제 허용' : 'URL 결제 중지'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
